import SumsubWebSdk from '@sumsub/websdk-react';
import { AnyEventName } from '@sumsub/websdk/types/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect } from 'react';

import { getKycAccessTokenApi } from '@flipster/apis/v1/kyc';
import { KycLevelTextMapper, ValidKycLevelType } from '@flipster/apis/v1/kyc/types';
import { SegmentPage } from '@flipster/features/src/misc/segment/segment-page';
import { useKYC } from '@flipster/features/src/shared/kyc';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { Spinner } from '@flipster/react/design-system/spinner';
import { center } from '@flipster/styled-system/patterns';
import { checkAxiosError } from '@flipster/utils/axios';
import { getErrorMessage } from '@flipster/utils/error';
import segment, { KycEvent } from '@flipster/utils/segment';

const OpenSumsubType = ['idCheck.onApplicantSubmitted', 'idCheck.onApplicantResubmitted'];
const IgnoreMessageType = ['idCheck.onResize'];

// TODO: [sh] 다국어 적용
const TooManyRequestsErrorMessage =
  'We are experiencing delays in the Identity Verification process due to concentrated traffic. Please try again in 12 hours. Thank you for your patience.';

interface Props {
  level: ValidKycLevelType;
  onSubmit?: () => void;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const KycModal = ({ isOpen, onClose, level, onSubmit, onExit }: Props) => {
  const t = useTranslations('Kyc.r250311');
  const locale = useSupportedLocale();
  const queryClient = useQueryClient();

  const { refetch } = useKYC();

  const {
    data: token,
    error,
    refetch: refetchGetKycAccessToken,
  } = useQuery({
    queryKey: ['getKycAccessToken', level],
    queryFn: () => getKycAccessTokenApi({ levelName: level }),
    select: (res) => res.data.accessToken,
    enabled: isOpen,
  });

  const onCloseModal = useCallback(() => {
    queryClient.removeQueries({ queryKey: ['getKycAccessToken', level] });
    onClose();
    refetch();
  }, [queryClient, level, onClose, refetch]);

  const onMessage = (type: AnyEventName, payload: object) => {
    if (!IgnoreMessageType.includes(type)) {
      segment.track(KycEvent.KycProgressEvent, {
        message_txt: type,
        kyc_payload: payload,
      });
    }
    if (OpenSumsubType.includes(type)) {
      onSubmit?.();
      onCloseModal();
    }
  };

  useEffect(() => {
    if (error) {
      if (checkAxiosError(error) && error.response?.status === 429) {
        snackbar.error({ message: TooManyRequestsErrorMessage });
      } else {
        snackbar.error({ message: getErrorMessage(error) });
      }
      onCloseModal();
    }
  }, [onCloseModal, error]);

  return (
    <>
      <SegmentPage name={KycEvent.KycPageView} />
      <Modal isOpen={isOpen} size='large' scrollable onExit={onExit}>
        <Modal.Close onClose={onCloseModal} />
        <Modal.Header>
          <Modal.Title label={t('Modal_sumsub_title', { level: KycLevelTextMapper[level] })} />
        </Modal.Header>
        <Modal.Content css={{ minH: { tablet: 424 } }}>
          {token ? (
            <SumsubWebSdk
              config={{ lang: locale || 'en' }}
              accessToken={token}
              expirationHandler={async () => {
                const { data } = await refetchGetKycAccessToken();
                return data ?? '';
              }}
              onMessage={onMessage}
            />
          ) : (
            <div className={center({ w: '100%', py: 160 })}>
              <Spinner />
            </div>
          )}
        </Modal.Content>
      </Modal>
    </>
  );
};
